import React, { ReactNode, useRef } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { ModelInput } from './types';
import { EqualitySymbol,PreReqs } from '../../../types/model';
import moment from 'moment';
import DecisionForm, { DecisionInput } from './DecisionForm';
import DenyForm, { DenyInput } from './_DenyForm/DenyForm';
import PriceForm, { PriceInput } from './PriceForm';
import RiskForm, { RiskInput } from './RiskForm';
import CredentialsForm, { CredentialsInput } from './Credentials';
import BaseModelForm from './BaseModelForm';
import {
    isPriceModel,
    isDecisionModel,
    isDenyModel,
    isRiskModel,
    Models,
    ModelType,
    isBidWinRateModel,
    isConversionModel
} from "../../../api/api-types";
import { getCredentials, toCredentials } from '../../../../../utils/models';
import BidWinRateForm from "./BidWinRateForm";
import ConversionForm ,{ConversionInput}from './ConversionForm';
import { scrollToFirstError } from '../../../../../utils/scrollToError';

type Props = {
    model?: Models;
    type: ModelType;
    onSubmit: (m: Models) => void;
    children: ReactNode;
};

const toInput = (type: ModelType, m?: Models) => ({
    name: m?.name ?? '',
    activeStart: moment(m?.activeStart) ?? moment(),
    activeEnd: moment(m?.activeEnd) ?? moment('12/31/2099', 'MM/DD/YYYY'),
    groupID: m?.groupId ?? null,
    groupWeight: m?.groupWeight ?? null,
    modelType: type,
    lowAmount: m?.lowAmount ?? 0,
    highAmount: m?.highAmount ?? 1000,
    preReqs: m?.preReqs?.map((x) => PreReqs[x]) ?? [],
});

export type ModelFormInput = ModelInput & DecisionInput & DenyInput & PriceInput & RiskInput & ConversionInput & CredentialsInput;

const toModel = (t: ModelType, m: ModelFormInput): Models => {
    const checkableModel = { ...m, modelType: t };
    const { modelType } = checkableModel;
    const buildDataRobot =
        modelType === 'Deny'
            ? null
            : { model: m.model, isLegacy: m.isLegacy, deployment: m.deployment, project: m.project };
    if (!modelType) {
        throw new Error('test');
    }

    //@ts-ignore
    return {
        modelId: 0,
        name: m.name,
        modelType: modelType,
        activeStart: m.activeStart?.format() ?? moment().format(),
        activeEnd: m.activeEnd?.format() ?? moment().format(),
        groupId: Number(m.groupID),
        groupWeight: Number(m.groupWeight),
        lowAmount: Number(m.lowAmount),
        highAmount: Number(m.highAmount),
        preReqs: m.preReqs.map((x) => (PreReqs as any)[x]),
        ...{
            ...(isBidWinRateModel(checkableModel)
              ? {
                      modelType: 'BidWinRate',
                      bidWinRate: {
                          minAmount: Number(m.minAmount),
                          maxAmount: Number(m.maxAmount),
                          dataRobot: toCredentials(buildDataRobot),
                      }
                }
              : { bidWinRate: null}),

            ...(isRiskModel(checkableModel)
                ? {
                      modelType: 'Risk',
                      risk: {
                          increment: Number(m.increment),
                          scoreMultiplier: Number(m.scoreMultiplier),
                          scoreModifier: Number(m.scoreModifier),
                          range: { low: Number(m.range?.low), high: Number(m.range?.high) },
                          dataRobot: toCredentials(buildDataRobot),
                      },
                  }
                : { risk: null }),

            ...(isPriceModel(checkableModel)
                ? {
                      modelType: 'Price',
                      price: {
                          minAmount: Number(m.minAmount),
                          maxAmount: Number(m.maxAmount),
                          slope: Number(m.slope),
                          intercept: Number(m.intercept),
                          dataRobot: toCredentials(buildDataRobot),
                          useSlopeIntercept: Boolean(m.useSlopeIntercept)
                      },
                  }
                : { price: null }),

                ...(isDenyModel(checkableModel)
                ? {
                    modelType: 'Deny',
                    denyRules:
                        m.denyRules?.map((rule, index) => ({
                            //Add needed null values and remove bad rules(rules w/o operator)

                            
                            ...rule,
                            notnull: rule.notnull
            }))  ?? null,
                  }
                : { denyRules: null }),

            ...(isDecisionModel(checkableModel)
                ? {
                      modelType: 'Decision',
                      decision: {
                          acceptLimit: Number(m.acceptLimit),
                          acceptSymbol: m.acceptSymbol as EqualitySymbol,
                          denyLimit: Number(m.denyLimit),
                          denySymbol: m.denySymbol as EqualitySymbol,
                          dataRobot: toCredentials(buildDataRobot),
                      },
                  }
                : { decision: null }),
                ...(isConversionModel(checkableModel)
                ? {
                      modelType: 'Conversion',
                      conversion: {
                          minAmount: Number(m.minAmount),
                          maxAmount: Number(m.maxAmount),
                          minValue: Number(m.minValue),
                          dataRobot: toCredentials(buildDataRobot),
                      },
                  }
                : { conversion: null }),
        },
    };
};

const ModelForm: React.FC<Props> = ({ model, type, children, onSubmit }) => {
    const defaultModel = toInput(type, model);
    const formRef = useRef<HTMLFormElement>(null);

    const methods = useForm<ModelFormInput>({
        defaultValues: defaultModel,
        mode: 'onChange',
        shouldFocusError: false,
    });

    function upsert(m: ModelFormInput) {
        const newModel = toModel(type, m);
        newModel.modelId = model?.modelId ?? 0;
        if (isDecisionModel(newModel) || isDenyModel(newModel) || isPriceModel(newModel) || isRiskModel(newModel) || isBidWinRateModel(newModel) || isConversionModel(newModel))
        {
            onSubmit(newModel);
        }
    }

    const checkableModel = model ?? { modelType: type };

    return (
        <FormProvider {...methods}>
            <form
                ref={formRef}
                onSubmit={methods.handleSubmit(
                    upsert,
                    (errors) => scrollToFirstError(formRef.current, errors)
                )}
            >

                <BaseModelForm />

                {(isDecisionModel(checkableModel) || isPriceModel(checkableModel)
                    || isRiskModel(checkableModel) || isBidWinRateModel(checkableModel) || isConversionModel(checkableModel)) && (
                    <CredentialsForm {...(checkableModel && getCredentials(checkableModel))} />
                )}

                {type === 'Decision' && isDecisionModel(checkableModel) && (
                    <DecisionForm {...checkableModel.decision} />
                )}

                {type === 'Deny' && isDenyModel(checkableModel) && (
                    <DenyForm preReqs={checkableModel.preReqs} denyRules={checkableModel.denyRules ?? []} />
                )}

                {type === 'Price' && isPriceModel(checkableModel) && <PriceForm {...checkableModel.price} />}

                {type === 'Risk' && isRiskModel(checkableModel) && <RiskForm {...checkableModel.risk} />}

                {type === 'BidWinRate' && isBidWinRateModel(checkableModel) && <BidWinRateForm {...checkableModel.bidWinRate} />}

                {type=== 'Conversion' && isConversionModel(checkableModel) && <ConversionForm {...checkableModel.conversion}/>}

                {children}
            </form>
        </FormProvider>
    );
};

export default ModelForm;
